"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArmErrorHandle = void 0;
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const constants_1 = require("../../../../common/constants");
const stringUtils_1 = require("../../../../common/stringUtils");
const azure_1 = require("../../../../error/azure");
const arm_1 = require("../../../../error/arm");
const innerHandleError_1 = require("./innerHandleError");
// constant string
const ErrorCodes = {
    InvalidTemplate: "InvalidTemplate",
    InvalidTemplateDeployment: "InvalidTemplateDeployment",
    ResourceGroupNotFound: "ResourceGroupNotFound",
    DeploymentOperationFailed: "DeploymentOperationFailed",
};
const filteredErrorMessage = "Template output evaluation skipped";
/* eslint-disable @typescript-eslint/no-namespace */
var ArmErrorHandle;
(function (ArmErrorHandle) {
    function fetchInnerError(error) {
        if (!error.details) {
            return error;
        }
        if (error.details.error) {
            return fetchInnerError(error.details.error);
        }
        else if (error.details instanceof Array && error.details[0]) {
            return fetchInnerError(error.details[0]);
        }
        return error;
    }
    ArmErrorHandle.fetchInnerError = fetchInnerError;
    async function handleArmDeploymentError(error, deployCtx) {
        // return the error if the template is invalid
        if (Object.keys(ErrorCodes).includes(error.code)) {
            if (error.code === ErrorCodes.InvalidTemplateDeployment) {
                error = fetchInnerError(error);
            }
            if (error.code === ErrorCodes.ResourceGroupNotFound) {
                return (0, teamsfx_api_1.err)(new azure_1.ResourceGroupNotExistError(deployCtx.resourceGroupName, deployCtx.client.subscriptionId));
            }
            else if (error.code === ErrorCodes.InvalidTemplate ||
                error.code === ErrorCodes.InvalidTemplateDeployment) {
                return (0, teamsfx_api_1.err)(new arm_1.DeployArmError(deployCtx.deploymentName, deployCtx.resourceGroupName, error));
            }
        }
        // try to get deployment error
        const result = await ArmErrorHandle.wrapGetDeploymentError(deployCtx, deployCtx.resourceGroupName, deployCtx.deploymentName, error);
        if (result.isOk()) {
            const deploymentError = result.value;
            // return thrown error if deploymentError is empty
            if (!deploymentError) {
                return (0, teamsfx_api_1.err)(new arm_1.DeployArmError(deployCtx.deploymentName, deployCtx.resourceGroupName, error));
            }
            const deploymentErrorObj = formattedDeploymentError(deploymentError);
            const deploymentErrorMessage = JSON.stringify(deploymentErrorObj, undefined, 2);
            let failedDeployments = [];
            if (deploymentError.subErrors) {
                failedDeployments = Object.keys(deploymentError.subErrors);
            }
            else {
                failedDeployments.push(deployCtx.deploymentName);
            }
            const format = failedDeployments.map((deployment) => deployment + " module");
            error.message = error.message + "\n" + deploymentErrorMessage;
            return (0, teamsfx_api_1.err)(new arm_1.DeployArmError(format.join(", "), deployCtx.resourceGroupName, error));
        }
        else {
            deployCtx.ctx.logProvider?.info(`origin error message is : \n${JSON.stringify(error, undefined, 2)}`);
            return result;
        }
    }
    ArmErrorHandle.handleArmDeploymentError = handleArmDeploymentError;
    async function wrapGetDeploymentError(deployCtx, resourceGroupName, deploymentName, rawError) {
        try {
            const deploymentError = await ArmErrorHandle.getDeploymentError(deployCtx, resourceGroupName, deploymentName);
            return (0, teamsfx_api_1.ok)(deploymentError);
        }
        catch (error) {
            return (0, teamsfx_api_1.err)(new arm_1.GetArmDeploymentError(deployCtx.deploymentName, deployCtx.resourceGroupName, rawError, error));
        }
    }
    ArmErrorHandle.wrapGetDeploymentError = wrapGetDeploymentError;
    async function getDeploymentError(deployCtx, resourceGroupName, deploymentName) {
        let deployment;
        try {
            deployment = await (0, innerHandleError_1.innerGetDeploymentError)(deployCtx.client, resourceGroupName, deploymentName);
        }
        catch (error) {
            if (deploymentName !== deployCtx.deploymentName &&
                error.code === constants_1.ConstantString.DeploymentNotFound) {
                return undefined;
            }
            throw error;
        }
        // The root deployment error name is deployCtx.deploymentName.
        // If we find the root error has a timestamp less than startTime, it is an old error to be ignored.
        // Other erros will be ignored as well.
        if (deploymentName === deployCtx.deploymentName &&
            deployment.properties?.timestamp &&
            deployment.properties.timestamp.getTime() < deployCtx.deploymentStartTime) {
            return undefined;
        }
        if (!deployment.properties?.error) {
            return undefined;
        }
        const deploymentError = {
            error: deployment.properties?.error,
        };
        const operations = [];
        const deploymentOperations = await (0, innerHandleError_1.innerGetDeploymentOperations)(deployCtx.client, resourceGroupName, deploymentName);
        for (const deploymentOperation of deploymentOperations) {
            operations.push(deploymentOperation);
        }
        for (const operation of operations) {
            if (operation.properties?.statusMessage?.error) {
                if (!deploymentError.subErrors) {
                    deploymentError.subErrors = {};
                }
                const name = operation.properties.targetResource?.resourceName ?? operation.id;
                deploymentError.subErrors[name] = {
                    error: operation.properties.statusMessage.error,
                };
                if (operation.properties.targetResource?.resourceType ===
                    constants_1.ConstantString.DeploymentResourceType &&
                    operation.properties.targetResource?.resourceName &&
                    operation.properties.targetResource?.id) {
                    const resourceGroupName = (0, stringUtils_1.getResourceGroupNameFromResourceId)(operation.properties.targetResource.id);
                    const subError = await getDeploymentError(deployCtx, resourceGroupName, operation.properties.targetResource?.resourceName);
                    if (subError) {
                        deploymentError.subErrors[name].inner = subError;
                    }
                }
            }
        }
        return deploymentError;
    }
    ArmErrorHandle.getDeploymentError = getDeploymentError;
    function formattedDeploymentError(deploymentError) {
        if (deploymentError.subErrors) {
            const result = {};
            for (const key in deploymentError.subErrors) {
                const subError = deploymentError.subErrors[key];
                if (subError.inner) {
                    result[key] = formattedDeploymentError(subError.inner);
                }
                else {
                    const needFilter = subError.error?.message?.includes(filteredErrorMessage) &&
                        subError.error?.code === ErrorCodes.DeploymentOperationFailed;
                    if (!needFilter) {
                        result[key] = subError.error;
                    }
                }
            }
            return result;
        }
        else {
            return deploymentError.error;
        }
    }
    ArmErrorHandle.formattedDeploymentError = formattedDeploymentError;
})(ArmErrorHandle || (exports.ArmErrorHandle = ArmErrorHandle = {}));
//# sourceMappingURL=handleError.js.map